/**
 * Lobby all-time leaderboard panels — left top-10 + right standings tail.
 * Adapted from WhatIs (`whatis/browser/src/ui/lobby-leaderboard.ts`).
 *
 * The panels share a glass-card visual language (`.an-lblb*`) so the side
 * leaderboard reuses these styles. setRows() is idempotent: repeated snapshot
 * frames don't restart the ping-pong scroll animation.
 */

import type { LbRow } from '@sg/shared';

export interface LobbyLeaderboardHandle {
  el: HTMLDivElement;
  setRows: (rows: LbRow[]) => void;
  dispose: () => void;
}

const STYLES = `
  .an-lblb {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.75) 100%);
    border: 2px solid var(--sg-primary, #FFC43D);
    border-radius: 14px;
    box-shadow: 0 4px 30px rgba(0,0,0,0.4);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    pointer-events: auto;
  }
  .an-lblb[data-role="lobby-lb-top"] { height: auto; }
  .an-lblb[data-role="lobby-lb-top"] .an-lblb-body { flex: 0 0 auto; overflow: visible; }
  .an-lblb[data-role="lobby-lb-top"] .an-lblb-list { padding: 4px 0 6px 0; }
  .an-lblb-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 14px rgba(255,196,61,0.4);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(255,196,61,0.35);
    flex: 0 0 auto;
  }
  .an-lblb-head {
    display: grid;
    grid-template-columns: 36px 1fr 70px 54px;
    gap: 8px;
    padding: 6px 10px 4px 10px;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    color: #94A3B8;
    border-bottom: 1px dashed rgba(255,255,255,0.15);
    flex: 0 0 auto;
  }
  .an-lblb-head > div:last-child,
  .an-lblb-head > div:nth-last-child(2) { text-align: right; }
  .an-lblb-body { flex: 1 1 auto; min-height: 0; overflow: hidden; position: relative; }
  .an-lblb-list { display: flex; flex-direction: column; padding: 4px 0; }
  .an-lblb-row {
    display: grid;
    grid-template-columns: 36px 1fr 70px 54px;
    gap: 8px;
    align-items: center;
    padding: 6px 10px;
    font-size: 0.95rem;
    border-top: 1px solid rgba(255,255,255,0.05);
  }
  .an-lblb-row:first-child { border-top: none; }
  .an-lblb-rank {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    text-align: center;
    font-size: 1.05rem;
    color: #94A3B8;
  }
  .an-lblb-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 600;
    color: #E2E8F0;
  }
  .an-lblb-points {
    text-align: right;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    font-variant-numeric: tabular-nums;
  }
  .an-lblb-games {
    text-align: right;
    color: #94A3B8;
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
  }
  .an-lblb-row.an-r1 { background: linear-gradient(90deg, rgba(255,196,61,0.18), rgba(255,196,61,0.0)); }
  .an-lblb-row.an-r2 { background: linear-gradient(90deg, rgba(192,192,192,0.15), rgba(192,192,192,0.0)); }
  .an-lblb-row.an-r3 { background: linear-gradient(90deg, rgba(205,127,50,0.15), rgba(205,127,50,0.0)); }
  .an-lblb-row.an-r1 .an-lblb-rank { color: var(--sg-primary, #FFD166); text-shadow: 0 0 10px rgba(255,196,61,0.7); }
  .an-lblb-row.an-r2 .an-lblb-rank { color: #D9D9D9; text-shadow: 0 0 8px rgba(217,217,217,0.5); }
  .an-lblb-row.an-r3 .an-lblb-rank { color: #E0A060; text-shadow: 0 0 8px rgba(205,127,50,0.5); }
  .an-lblb-empty {
    padding: 18px 12px;
    text-align: center;
    color: #94A3B8;
    font-style: italic;
    font-size: 0.9rem;
  }

  @keyframes an-lblb-pingpong {
    0%   { transform: translateY(0); }
    100% { transform: translateY(var(--an-lblb-travel, 0px)); }
  }
  .an-lblb-scroll {
    animation: an-lblb-pingpong var(--an-lblb-duration, 40s) ease-in-out infinite alternate;
    will-change: transform;
  }
`;

export function ensureLobbyLbStyles(): void {
  if (document.getElementById('an-lblb-style')) return;
  const s = document.createElement('style');
  s.id = 'an-lblb-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function emptyMarkup(text: string): string {
  return `<div class="an-lblb-empty">${text}</div>`;
}

function medalFor(rank: number): string {
  if (rank === 1) return 'an-r1';
  if (rank === 2) return 'an-r2';
  if (rank === 3) return 'an-r3';
  return '';
}

function rowKey(r: LbRow): string {
  return r.player_id != null ? `p:${r.player_id}` : `r:${r.rank}`;
}

function rowsSignature(rows: LbRow[]): string {
  let s = '';
  for (const r of rows) {
    s += `${r.player_id ?? ''},${r.rank},${r.total_points},${r.games_played},${r.display_name}|`;
  }
  return s;
}

function renderRow(r: LbRow, medalClass: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = `an-lblb-row ${medalClass}`.trim();
  row.innerHTML = `
    <div class="an-lblb-rank">${r.rank}</div>
    <div class="an-lblb-name" data-role="name"></div>
    <div class="an-lblb-points">${r.total_points.toLocaleString()}</div>
    <div class="an-lblb-games">${r.games_played}</div>
  `;
  const nameEl = row.querySelector<HTMLDivElement>('[data-role="name"]')!;
  nameEl.textContent = r.display_name;
  return row;
}

function updateRow(row: HTMLDivElement, r: LbRow, medalClass: string): void {
  const desiredCls = `an-lblb-row ${medalClass}`.trim();
  if (row.className !== desiredCls) row.className = desiredCls;
  const rankEl = row.querySelector<HTMLDivElement>('.an-lblb-rank');
  const nameEl = row.querySelector<HTMLDivElement>('[data-role="name"]');
  const pointsEl = row.querySelector<HTMLDivElement>('.an-lblb-points');
  const gamesEl = row.querySelector<HTMLDivElement>('.an-lblb-games');
  if (!rankEl || !nameEl || !pointsEl || !gamesEl) return;
  if (rankEl.textContent !== String(r.rank)) rankEl.textContent = String(r.rank);
  const pts = r.total_points.toLocaleString();
  if (pointsEl.textContent !== pts) pointsEl.textContent = pts;
  const games = String(r.games_played);
  if (gamesEl.textContent !== games) gamesEl.textContent = games;
  if (nameEl.textContent !== r.display_name) nameEl.textContent = r.display_name;
}

export function createLobbyLeaderboardTop(mount: HTMLElement): LobbyLeaderboardHandle {
  ensureLobbyLbStyles();
  const el = document.createElement('div');
  el.className = 'an-lblb';
  el.setAttribute('data-role', 'lobby-lb-top');
  el.innerHTML = `
    <div class="an-lblb-title">TOP 10 ALL-TIME</div>
    <div class="an-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div><div>GAMES</div></div>
    <div class="an-lblb-body"><div class="an-lblb-list" data-role="list"></div></div>
  `;
  mount.appendChild(el);
  const list = el.querySelector<HTMLDivElement>('[data-role="list"]')!;
  let lastSig = '';
  let nodesByKey = new Map<string, HTMLDivElement>();
  let isEmpty = false;

  function setRows(rows: LbRow[]): void {
    const top = rows.slice(0, 10);
    const sig = rowsSignature(top);
    if (sig === lastSig) return;
    lastSig = sig;
    if (top.length === 0) {
      if (!isEmpty) {
        list.innerHTML = emptyMarkup('No scores yet — be the first!');
        nodesByKey = new Map();
        isEmpty = true;
      }
      return;
    }
    const nextNodes = new Map<string, HTMLDivElement>();
    const fragment = document.createDocumentFragment();
    for (const r of top) {
      const key = rowKey(r);
      const medal = medalFor(r.rank);
      let node = nodesByKey.get(key);
      if (node) updateRow(node, r, medal);
      else node = renderRow(r, medal);
      nextNodes.set(key, node);
      fragment.appendChild(node);
    }
    if (isEmpty) { list.innerHTML = ''; isEmpty = false; }
    list.replaceChildren(fragment);
    nodesByKey = nextNodes;
  }

  return { el, setRows, dispose() { el.remove(); } };
}

export function createLobbyLeaderboardTail(mount: HTMLElement): LobbyLeaderboardHandle {
  ensureLobbyLbStyles();
  const el = document.createElement('div');
  el.className = 'an-lblb';
  el.setAttribute('data-role', 'lobby-lb-tail');
  el.innerHTML = `
    <div class="an-lblb-title">STANDINGS</div>
    <div class="an-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div><div>GAMES</div></div>
    <div class="an-lblb-body"><div class="an-lblb-scroll" data-role="scroll"><div class="an-lblb-list" data-role="list"></div></div></div>
  `;
  mount.appendChild(el);
  const body = el.querySelector<HTMLDivElement>('.an-lblb-body')!;
  const scroll = el.querySelector<HTMLDivElement>('[data-role="scroll"]')!;
  const list = el.querySelector<HTMLDivElement>('[data-role="list"]')!;
  let resizeObs: ResizeObserver | null = null;
  let lastSig = '';
  let nodesByKey = new Map<string, HTMLDivElement>();
  let isEmpty = false;

  function recomputeTravel(): void {
    const overflow = Math.max(0, scroll.scrollHeight - body.clientHeight);
    scroll.style.setProperty('--an-lblb-travel', `${-overflow}px`);
    const dur = Math.max(35, Math.round(overflow / 80));
    scroll.style.setProperty('--an-lblb-duration', `${dur}s`);
  }

  function setRows(rows: LbRow[]): void {
    const tail = rows.slice(10);
    const sig = rowsSignature(tail);
    if (sig === lastSig) return;
    lastSig = sig;
    if (tail.length === 0) {
      if (!isEmpty) {
        list.innerHTML = emptyMarkup('Only top 10 so far — more to come!');
        nodesByKey = new Map();
        isEmpty = true;
        requestAnimationFrame(recomputeTravel);
      }
      return;
    }
    const nextNodes = new Map<string, HTMLDivElement>();
    const fragment = document.createDocumentFragment();
    for (const r of tail) {
      const key = rowKey(r);
      let node = nodesByKey.get(key);
      if (node) updateRow(node, r, '');
      else node = renderRow(r, '');
      nextNodes.set(key, node);
      fragment.appendChild(node);
    }
    if (isEmpty) { list.innerHTML = ''; isEmpty = false; }
    list.replaceChildren(fragment);
    nodesByKey = nextNodes;
    requestAnimationFrame(recomputeTravel);
  }

  if (typeof ResizeObserver !== 'undefined') {
    resizeObs = new ResizeObserver(() => recomputeTravel());
    resizeObs.observe(body);
  }

  return { el, setRows, dispose() { if (resizeObs) resizeObs.disconnect(); el.remove(); } };
}
